import { useWindowVirtualizer } from "@tanstack/react-virtual";
import { axiosInstance } from "../auth/Auth";
import moment from "moment/min/moment-with-locales";
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Collapse, Fade, Form, InputGroup, Row, Stack, ToggleButtonGroup, ToggleButton } from "react-bootstrap";
import { CaretDownFill, CaretUpFill, Funnel, FunnelFill, Search } from "react-bootstrap-icons";
import { Await, useLoaderData, useRouteLoaderData } from "react-router-dom";
import Break from "../components/Breaks/Break";
import BreaksHeader from "../components/Breaks/BreaksHeader";
import ContentGroupBalance from "../components/ContentGroupBalance";
import ContentGroupTrend from "../components/ContentGroupTrend";
import DataDescription from "../components/DataDescription";
import EmptyRow from "../components/EmptyRow";
import { average, getRadioName, mapItemType, toAutoDecimals } from "../components/HelperFunctions";
import { useFetchData, useLatestCalculatedDate, useSetDataTypeOnStationChange, useSetDaypartOnChange } from "../components/HelperHooks";
import PageDesciption from "../components/PageDesciption";
import ContentGroupsPicker from "../components/Pickers/ContentGroupsPicker";
import DataTypePicker from "../components/Pickers/DataTypePicker";
import DatesPicker, { latestFull2Weeks, latestFullMonth, latestFullQuarter, latestFullWeek } from "../components/Pickers/DatesPicker";
import DaypartPicker from "../components/Pickers/DaypartPicker";
import DaysPicker from "../components/Pickers/DaysPicker";
import HoursPicker from "../components/Pickers/HoursPicker";
import ItemTypesPicker from "../components/Pickers/ItemTypesPicker";
import StationPicker from "../components/Pickers/StationPicker";
import RATooltip from "../components/RATooltip";
import { useUserFormState } from "../components/UserSelection";
import { itemTypes } from "../components/constants";
import { DataContent, DescriptionWithDownloadRow, InputSelection, ModulePage, VirtualizedWindowContainer } from "../components/pageSections";
import useSortableData from "../components/useSortableData";
import ConfirmPrompt from "../components/ConfirmPrompt";
import { BarChartLineFill, FolderPlus } from "react-bootstrap-icons";
import MassAddContentGroupsModal from "../components/MassAddContentGroupsModal";
import DurationPicker from "../components/Pickers/DurationPicker";

const extraDefaults = { itemTypes: itemTypes.filter(x => x.id !== "w"), showAllBreaks: "false" };

export default function ContentTesting() {
    const moduleName = useMemo(() => "contentTesting", []);
    const moduleId = useMemo(() => ({ 1: 12, 2: 12 }), []);
    const { radios } = useRouteLoaderData("root");
    const { contentGroups } = useLoaderData();
    const [formData, setFormData, saveFormData, loadFormData] = useUserFormState(moduleName, extraDefaults);
    const [loading, errorCode, data, fetchData, setData] = useFetchData();
    const [latestCalculatedDate, setLatestCalculatedDate] = useState(null);
    const [resolvedContentGroups, setResolvedContentGroups] = useState(null);
    const [selectedContentGroupId, setSelectedContentGroupId] = useState(null);
    const [contentGroupsFilter, setContentGroupsFilter] = useState({});
    const [contentGroupsFilterOpen, setContentGroupsFilterOpen] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [filterData, setFilterData] = useState({
        contentGroups: [],
        keywords: "",
        limit: 0,
        hideInvalid: false,
        showAllBreaks: false,
        showExcluded: false,
        minDuration: 0,
        maxDuration: 0,
    });
    const tableContainerRef = useRef(null);
    const advancedInputRef = useRef(null);
    const [selectedKeywords, setSelectedKeywords] = useState(null);
    const [showFilteredTrend, setShowFilteredTrend] = useState(false);
    const [showMassAddModal, setShowMassAddModal] = useState(false);
    const [filterFunc, setFilterFunc] = useState(() => () => true);
    const [percentageGrouping, setPecentageGrouping] = useState("breaks");

    const predefinedRanges = useMemo(() => {
        if (latestCalculatedDate) {
            if (formData.dateRange === undefined) {
                setFormData(current => ({ ...current, dateRange: latestFullWeek(latestCalculatedDate, true) }));
            }
            return [
                latestFullWeek(latestCalculatedDate),
                latestFull2Weeks(latestCalculatedDate),
                latestFullMonth(latestCalculatedDate),
                latestFullQuarter(latestCalculatedDate),
            ];
        }
    }, [formData.dateRange, latestCalculatedDate, setFormData]);

    const breaksDataMemo = useMemo(() => {
        if (data) {
            let filteredData = data.breaks;
            if (filterData.contentGroups.length > 0) {
                filteredData = filteredData.filter(x => x.content_groups.some(cg => filterData.contentGroups.map(y => y.group_id).includes(cg)));
            }
            if (filterData.hideInvalid) {
                filteredData = filteredData.filter(x => x.invalid === false);
            }
            if (!filterData.showAllBreaks) {
                filteredData = filteredData.filter(x => x.hidden === false);
            }

            if (Array.isArray(data.excluded_days)) {
                filteredData = filteredData.map(x => {
                    const itemDate = moment(x.start_datetime).format("YYYY-MM-DD");
                    const isExcluded = data.excluded_days.includes(itemDate);
                    return { ...x, excluded: isExcluded };
                });

                if (!filterData.showExcluded) {
                    filteredData = filteredData.filter(x => !x.excluded);
                }
            }
            if (filterData.keywords) {
                const searchKeywords = filterData.keywords.split(",").filter(x => x.trim());
                filteredData = filteredData.filter(breakItem => {
                    return searchKeywords.some(searchKeyword => {
                        if (searchKeyword.startsWith('"') && searchKeyword.endsWith('"')) {
                            const exactKeyword = searchKeyword.slice(1, -1).toLowerCase().trim();
                            return breakItem.keywords.some(breakKeyword => breakKeyword.toLowerCase().split(" ").includes(exactKeyword));
                        } else if (searchKeyword.includes("+")) {
                            const [firstWord, secondWord] = searchKeyword
                                .toLowerCase()
                                .split("+")
                                .map(word => word.trim());
                            return (
                                breakItem.keywords.some(breakKeyword => breakKeyword.toLowerCase().includes(firstWord)) &&
                                breakItem.keywords.some(breakKeyword => breakKeyword.toLowerCase().includes(secondWord))
                            );
                        } else {
                            return (
                                !searchKeyword.trim() ||
                                breakItem.keywords.some(breakKeyword => breakKeyword.toLowerCase().includes(searchKeyword.toLowerCase().trim()))
                            );
                        }
                    });
                });
            }
            return filteredData;
        }
    }, [data, filterData]);

    const { sortedItems, requestSort, sortConfig } = useSortableData(breaksDataMemo, { key: "start_datetime", direction: "ascending" }, filterData.limit);

    const csvHeaders = [
        "Start Datetime",
        "End Datetime",
        "Duration (s)",
        data?.data_type === 1 ? "Score" : "Delta",
        "Break Type",
        "Keywords",
        "Content Groups",
        "Summary",
    ];
    const csvData = useMemo(
        () =>
            sortedItems?.map(x => [
                x.start_datetime,
                x.end_datetime,
                x.duration,
                data.data_type === 1 ? x.score : x.delta,
                mapItemType(x.item_type),
                x.keywords.join(", "),
                x.content_groups.map(cg => resolvedContentGroups?.find(g => g.group_id === cg)?.display_name).join(", "),
                x.synopsis,
            ]),
        [data?.data_type, resolvedContentGroups, sortedItems]
    );

    useEffect(() => {
        async function resolveContentGroups() {
            const groups = (await contentGroups).data;
            setResolvedContentGroups(groups);
            const filterDict = groups.reduce((acc, obj) => {
                acc[obj.group_id] = true;
                return acc;
            }, {});
            filterDict[-1] = true;
            setContentGroupsFilter(filterDict);
        }
        resolveContentGroups();
    }, [contentGroups]);

    const totals = useMemo(() => {
        if (!data?.breaks) return;

        let filteredData = data.breaks;
        if (filterData.hideInvalid) {
            filteredData = filteredData.filter(x => !x.invalid);
        }
        if (!filterData.showAllBreaks) {
            filteredData = filteredData.filter(x => !x.hidden);
        }
        if (Array.isArray(data.excluded_days) && !filterData.showExcluded) {
            filteredData = filteredData.filter(x => {
                const itemDate = moment(x.start_datetime).format("YYYY-MM-DD");
                return !data.excluded_days.includes(itemDate);
            });
        }

        return {
            score: average(filteredData.filter(x => !x.invalid && x.score > 0).map(x => x.score)),
            delta: average(filteredData.filter(x => !x.invalid).map(x => x.delta)),
            duration: average(filteredData.map(x => x.duration)),
            count: filteredData.length,
        };
    }, [data, filterData]);

    const filteredTotals = useMemo(() => {
        if (!breaksDataMemo) return;
        return {
            score: average(breaksDataMemo.filter(x => !x.invalid && x.score > 0).map(x => x.score)),
            delta: average(breaksDataMemo.filter(x => !x.invalid).map(x => x.delta)),
            duration: average(breaksDataMemo.map(x => x.duration)),
            count: breaksDataMemo.length,
        };
    }, [breaksDataMemo]);

    const limitedTotals = useMemo(() => {
        if (!sortedItems) return;
        return {
            score: average(sortedItems.filter(x => !x.invalid && x.score > 0).map(x => x.score)),
            delta: average(sortedItems.filter(x => !x.invalid).map(x => x.delta)),
            duration: average(sortedItems.map(x => x.duration)),
            count: sortedItems.length,
        };
    }, [sortedItems]);

    const demo = useMemo(() => [{ data_type: formData.dataType, listener_group: 0 }], [formData.dataType]);
    useLatestCalculatedDate(setLatestCalculatedDate, moduleId, formData.station, demo);
    useSetDaypartOnChange(setFormData, formData.station, formData.days, formData.hourRange);
    useSetDataTypeOnStationChange(setFormData, formData.station);

    async function handleFormSubmit(event) {
        if (event) event.preventDefault();
        advancedInputRef.current.closeAdvanced();
        await saveFormData();
        await saveFormData(formData.station[0].radio_id);
        const input = {
            radio_id: formData.station[0].radio_id,
            start_datetime: moment(formData.dateRange[0]).startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
            end_datetime: moment(formData.dateRange[1]).endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
            data_type: formData.dataType,
            item_types: formData.itemTypes.map(x => x.id),
            start_time: moment().hours(formData.hourRange[0]).minutes(0).seconds(0).format("HH:mm:ss"),
            end_time: moment()
                .hours(formData.hourRange[1] - 1)
                .minutes(59)
                .seconds(59)
                .format("HH:mm:ss"),
            days: formData.days,
            min_duration: formData.minDuration,
            max_duration: formData.maxDuration,
        };
        await fetchData("/break_deltas/?ascending=false", input);
        if (formData.station?.length && resolvedContentGroups) {
            setFilterFunc(() => createPrioritisedFilter(formData, resolvedContentGroups));
        }
        setFilterData(prev => ({
            ...prev,
            contentGroups: [],
            keywords: "",
        }));
    }

    const handleUnhideAllBreaks = () => {
        unhideAllBreaks();
        setShowConfirm(false);
    };

    async function unhideAllBreaks() {
        try {
            let hiddenBreaks = data.breaks.filter(b => b.hidden === true).map(b => b.break_id);
            await axiosInstance.post(`/content_testing/breaks/toggle_hide?hidden=${false}`, hiddenBreaks);
            const updatedBreaks = data.breaks.map(b => ({
                ...b,
                hidden: b.hidden === true ? false : b.hidden,
            }));
            setData({ ...data, breaks: updatedBreaks });
        } catch (error) {
            console.error(error);
        }
    }

    const virtualizer = useWindowVirtualizer({
        count: sortedItems?.length || 1,
        estimateSize: useCallback(i => (sortedItems?.length > 0 && sortedItems[i]?.item_type !== "c" ? 84 : 52), [sortedItems]),
        getItemKey: useCallback(i => sortedItems?.length && sortedItems[i].break_id, [sortedItems]),
        overscan: 30,
        scrollMargin: tableContainerRef.current?.offsetTop ?? 0,
    });

    function createPrioritisedFilter(formData, contentGroups) {
        return function filterFunction(x) {
            const selectedRadioId = formData.station?.[0]?.radio_id ?? "";
            const hasBasicWithSelected = contentGroups.some(g => g.basic && g.radio_id === selectedRadioId);
            if (hasBasicWithSelected) return x.basic && x.radio_id === selectedRadioId;
            else {
                return (x.basic && x.radio_id === null) || (!x.basic && x.radio_id === selectedRadioId);
            }
        };
    }

    function formatDuration(totalSeconds = 0) {
        const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
        const seconds = String(totalSeconds % 60).padStart(2, "0");
        return `${minutes}:${seconds}`;
    }

    return (
        <ModulePage>
            <ModulePage.Title>Content Testing</ModulePage.Title>
            <ModulePage.InputSelection
                handleFormSubmit={handleFormSubmit}
                disabled={
                    !formData.station.length || !formData.dateRange || !formData.days.length || !formData.hourRange?.length || !formData.itemTypes?.length
                }>
                <InputSelection.Main>
                    <StationPicker
                        formData={formData}
                        setFormData={setFormData}
                        name={moduleName}
                        moduleFilterFunction={x => x.module_id === 11}
                        filterFunction={x => x.mtt_only === false}
                        loadOnChange={loadFormData}
                    />
                    <DatesPicker formData={formData} setFormData={setFormData} predefinedRanges={predefinedRanges} latestAvailableDate={latestCalculatedDate} />
                    <DaypartPicker formData={formData} setFormData={setFormData} name={moduleName} />
                    <DataTypePicker formData={formData} setFormData={setFormData} name={moduleName} />
                </InputSelection.Main>
                <InputSelection.Advanced moduleName={moduleName} ref={advancedInputRef}>
                    <ItemTypesPicker formData={formData} setFormData={setFormData} name={moduleName} filterFunction={x => x.id !== "w"} />
                    <DaysPicker formData={formData} setFormData={setFormData} name={moduleName} />
                    <HoursPicker formData={formData} setFormData={setFormData} name={moduleName} />
                    <DurationPicker filterData={formData} setFilterData={setFormData} name={moduleName} />
                </InputSelection.Advanced>
            </ModulePage.InputSelection>
            <ModulePage.DataContent loading={loading} errorCode={errorCode} customErrorMessage={errorCode?.message}>
                <DataContent.Placeholder show={!data}>
                    <PageDesciption
                        title={"Content Testing"}
                        traInfo={[
                            {
                                text: "Officially supported languages are English and German. Other languages are currently in testing – please note this when checking data for keywords and categories. If you have any questions or would like to help us fast track the tuning for your language, reach out to your RadioAnalyzer contact or ",
                                url: "support@radioanalyzer.com ",
                            },
                        ]}
                        whatList={[
                            "All spoken word is automatically summarised and categorized",
                            "Breaks are categorized by content and have keywords to further refine your search",
                            "Choose specific types of content like talk or news, or filter by category or keyword to make easy searches",
                            "Please note: Content Testing requires precise play-logs from your station for optimal performance",
                        ]}
                        knowList={[
                            { text: "You can click on a graph to see how content group performed over time" },
                            { text: "Custom content groups and keywords can be added or edited. If you make a mistake, hit the reset button" },
                            { text: "The average duration and score above the table changes based on your current filter" },
                            { text: "Content testing only runs 6-18:00 on breaks between 45seconds and 8 minutes" },
                        ]}
                        relatedLinks={[
                            {
                                title: "Topic Testing – a Beast of a Weapon",
                                url: "https://radioanalyzer.com/blog/coming-soon-topic-testing-a-beast-of-a-weapon",
                            },
                        ]}
                        videoSrc=""
                    />
                </DataContent.Placeholder>
                {data && (
                    <>
                        {data.content_balance.length > 1 && (
                            <>
                                <Row className="g-2 align-items-center">
                                    <Col xs={12} md="auto" className="order-1">
                                        <ToggleButtonGroup
                                            type="radio"
                                            name="cgb-grouping"
                                            className="ra-switcher w-100"
                                            onChange={val => setPecentageGrouping(val)}
                                            value={percentageGrouping}>
                                            <ToggleButton id="percentage-breaks" value="breaks" variant="ra-dark-blue-outline" className="px-1">
                                                % Breaks
                                            </ToggleButton>
                                            <ToggleButton id="percentage-duration" value="duration" variant="ra-dark-blue-outline" className="px-1">
                                                % Duration
                                            </ToggleButton>
                                        </ToggleButtonGroup>
                                    </Col>

                                    <Col xs={12} md="auto" className="order-2 order-md-3 ">
                                        <Button
                                            variant="ra-yellow"
                                            size="sm"
                                            className="d-flex align-items-center w-100 justify-content-center justify-content-md-start"
                                            onClick={() => setContentGroupsFilterOpen(!contentGroupsFilterOpen)}
                                            aria-controls="content-groups-filter"
                                            aria-expanded={contentGroupsFilterOpen}>
                                            {!Object.values(contentGroupsFilter).every(v => v === true) &&
                                            !Object.values(contentGroupsFilter).every(v => v === false) ? (
                                                <FunnelFill />
                                            ) : (
                                                <Funnel />
                                            )}
                                            <span className="mx-2">Content Groups Filter</span>
                                            {contentGroupsFilterOpen ? <CaretUpFill /> : <CaretDownFill />}
                                        </Button>
                                    </Col>
                                    <Col xs={12} md="auto" className="order-3 order-md-2 ms-md-auto">
                                        {contentGroupsFilterOpen ? (
                                            <Fade in={contentGroupsFilterOpen} appear={true}>
                                                <Stack
                                                    direction="horizontal"
                                                    gap={2}
                                                    className="justify-content-center justify-content-md-end mt-2 mt-md-0 w-100">
                                                    <Button
                                                        variant="ra-yellow"
                                                        size="sm"
                                                        onClick={() =>
                                                            setContentGroupsFilter(Object.fromEntries(Object.keys(contentGroupsFilter).map(key => [key, true])))
                                                        }>
                                                        Select All
                                                    </Button>
                                                    <Button
                                                        variant="ra-yellow"
                                                        size="sm"
                                                        onClick={() =>
                                                            setContentGroupsFilter(
                                                                Object.fromEntries(Object.keys(contentGroupsFilter).map(key => [key, false]))
                                                            )
                                                        }>
                                                        Deselect All
                                                    </Button>
                                                </Stack>
                                            </Fade>
                                        ) : null}
                                    </Col>
                                </Row>

                                <Collapse in={contentGroupsFilterOpen}>
                                    <div id="content-groups-filter">
                                        <Row className="row-cols-2 row-cols-md-3 row-cols-lg-4 row-cols-xl-5 row-cols-xxl-6 my-2">
                                            <Col>
                                                <Form.Switch
                                                    id={`content-group-filter-${-1}`}
                                                    label={"Other"}
                                                    checked={contentGroupsFilter[-1]}
                                                    onChange={e =>
                                                        setContentGroupsFilter({
                                                            ...contentGroupsFilter,
                                                            [-1]: e.target.checked,
                                                        })
                                                    }
                                                />
                                            </Col>
                                            {resolvedContentGroups
                                                .filter(x => new Set(data.content_balance.map(y => y.content_group_id)).has(x.group_id))
                                                .map(x => (
                                                    <Col key={x.group_id}>
                                                        <Form.Switch
                                                            id={`content-group-filter-${x.group_id}`}
                                                            label={x.display_name}
                                                            checked={contentGroupsFilter[x.group_id]}
                                                            onChange={e =>
                                                                setContentGroupsFilter({
                                                                    ...contentGroupsFilter,
                                                                    [x.group_id]: e.target.checked,
                                                                })
                                                            }
                                                        />
                                                    </Col>
                                                ))}
                                        </Row>
                                    </div>
                                </Collapse>

                                <Suspense>
                                    <Await resolve={radios}>
                                        {resolvedRadios => (
                                            <ContentGroupBalance
                                                data={data?.content_balance}
                                                percentageGrouping={percentageGrouping}
                                                contentGroups={resolvedContentGroups}
                                                setSelectedGroupId={setSelectedContentGroupId}
                                                selectedContentGroups={Object.keys(contentGroupsFilter)
                                                    .filter(x => contentGroupsFilter[x] === true)
                                                    .map(Number)}
                                                radioName={getRadioName(data.radio_id, resolvedRadios.data)}
                                                subtitle={`${moment(data.start_datetime).format("L")} - ${moment(data.end_datetime).format("L")} | ${moment(
                                                    data.start_time,
                                                    "HH:mm:ss"
                                                ).format("LT")} - ${moment(data.end_time, "HH:mm:ss").format("LT")}`}
                                            />
                                        )}
                                    </Await>
                                </Suspense>
                                <ContentGroupTrend
                                    show={selectedContentGroupId}
                                    onHide={() => setSelectedContentGroupId(null)}
                                    radioId={data.radio_id}
                                    contentGroupId={selectedContentGroupId}
                                    dataType={data.data_type}
                                    demoId={1}
                                    monthsLimit={0}
                                    contentGroups={resolvedContentGroups}
                                    itemTypes={formData.itemTypes.map(x => x.id)}
                                    daypart={formData.daypart[0]}
                                    days={formData.days}
                                    start_time={moment().hours(formData.hourRange[0]).minutes(0).seconds(0).format("HH:mm:ss")}
                                    end_time={moment()
                                        .hours(formData.hourRange[1] - 1)
                                        .minutes(59)
                                        .seconds(59)
                                        .format("HH:mm:ss")}
                                />
                            </>
                        )}
                        <Row className="row-gap-3 mb-3">
                            <Form.Group as={Col} className="col-12 col-sm-5 col-lg-5 col-xl-5">
                                <Form.Label className=" d-inline-flex">
                                    Keywords
                                    <RATooltip className="ms-1">
                                        Use quotes for exact matches. For example, searching for <i>"king"</i> will find <i>King Charles</i> but not{" "}
                                        <i>Talking</i>. Commas can be used as <i>OR</i>.
                                    </RATooltip>
                                </Form.Label>
                                <InputGroup>
                                    <InputGroup.Text className="input-icon">
                                        <Search />
                                    </InputGroup.Text>
                                    <Form.Control value={filterData.keywords} onChange={e => setFilterData({ ...filterData, keywords: e.target.value })} />
                                </InputGroup>
                            </Form.Group>
                            <ContentGroupsPicker
                                formData={filterData}
                                setFormData={setFilterData}
                                name={moduleName}
                                filterFunction={filterFunc}
                                customClass={"col-12 col-sm-4 col-lg-5 col-xl-6 col-xxl-6"}
                            />
                            <Form.Group as={Col} className="col-12 col-sm-3 col-lg-2 col-xl-1 col-xxl-1">
                                <Form.Label className=" d-inline-flex align-items-center ">
                                    <Funnel className="me-1 fw-bold"></Funnel>
                                    Limit
                                </Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        value={filterData.limit}
                                        type="number"
                                        min={0}
                                        onChange={e => setFilterData({ ...filterData, limit: e.target.value })}
                                    />
                                </InputGroup>
                            </Form.Group>
                        </Row>
                        <Row className="pt-1 pb-2">
                            <Form.Group as={Col} className=" col-auto d-flex flex-row align-items-center mb-2 mb-xl-0 ">
                                <Button
                                    variant={"ra-dark-blue"}
                                    className="d-flex align-items-center column-gap-1 "
                                    size="md"
                                    onClick={() => {
                                        const modifiedKeywords = filterData.keywords?.replace(/"/g, '\\"');
                                        setSelectedKeywords(modifiedKeywords ?? "");
                                        setShowFilteredTrend(true);
                                    }}
                                    disabled={formData.dataType === 2}>
                                    <BarChartLineFill /> Filtered Trend
                                </Button>
                                {showFilteredTrend && (
                                    <ContentGroupTrend
                                        show={showFilteredTrend}
                                        onHide={() => setShowFilteredTrend(false)}
                                        radioId={data.radio_id}
                                        keywords={selectedKeywords}
                                        dataType={data.data_type}
                                        demoId={1}
                                        contentGroups={resolvedContentGroups}
                                        selectedContentGroupIds={filterData.contentGroups.map(item => item.group_id)}
                                        filtered={true}
                                        itemTypes={formData.itemTypes.map(x => x.id)}
                                        includeHidden={filterData.showAllBreaks}
                                        includeInvalid={filterData.hideInvalid}
                                        daypart={formData.daypart[0]}
                                        days={formData.days}
                                        start_time={moment().hours(formData.hourRange[0]).minutes(0).seconds(0).format("HH:mm:ss")}
                                        end_time={moment()
                                            .hours(formData.hourRange[1] - 1)
                                            .minutes(59)
                                            .seconds(59)
                                            .format("HH:mm:ss")}
                                    />
                                )}
                            </Form.Group>
                            <Form.Group as={Col} className="col-auto d-flex flex-row align-items-center  mb-2 mb-xl-0">
                                <Button size="md" onClick={() => setShowConfirm(true)} variant={"ra-dark-blue"} title="Reset all hidden.">
                                    Reset all hidden
                                </Button>
                            </Form.Group>
                            <ConfirmPrompt
                                show={showConfirm}
                                handleClose={() => setShowConfirm(false)}
                                handleConfirm={handleUnhideAllBreaks}
                                bodyText={
                                    "'Yes' will make all hidden breaks visible again within your selection, and their 'hidden' status will be removed. Do you want to proceed?"
                                }
                                confirmText="Yes"
                                cancelText="No"
                            />
                            <Form.Group controlId={`${moduleName}-display-breaks`} className="col-auto d-flex flex-row align-items-center mt-2">
                                <Form.Label className="pe-2">Show hidden</Form.Label>
                                <Form.Switch
                                    id={`${moduleName}-display-breaks`}
                                    checked={filterData.showAllBreaks}
                                    className="mb-1"
                                    onChange={e =>
                                        setFilterData({
                                            ...filterData,
                                            showAllBreaks: e.target.checked,
                                        })
                                    }
                                />
                            </Form.Group>
                            <Form.Group as={Col} controlId={`${moduleName}-hide-invalid`} className="col-auto d-flex flex-row align-items-center mt-2  ">
                                <Form.Label className="pe-2">Hide Invalid</Form.Label>
                                <Form.Switch
                                    id={`${moduleName}-hide-invalid`}
                                    checked={filterData.hideInvalid}
                                    className="mb-1"
                                    onChange={e =>
                                        setFilterData({
                                            ...filterData,
                                            hideInvalid: e.target.checked,
                                        })
                                    }
                                />
                            </Form.Group>
                            <Form.Group as={Col} controlId={`${moduleName}-show-excluded`} className="col-auto d-flex flex-row align-items-center mt-2  ">
                                <Form.Label className="pe-2">Show Excluded</Form.Label>
                                <Form.Switch
                                    id={`${moduleName}-show-excluded`}
                                    checked={filterData.showExcluded}
                                    className="mb-1"
                                    onChange={e =>
                                        setFilterData({
                                            ...filterData,
                                            showExcluded: e.target.checked,
                                        })
                                    }
                                />
                            </Form.Group>
                            <Col className="d-flex justify-content-end">
                                <Form.Group as={Col} className="col-auto d-flex align-items-center ml-auto">
                                    <Button
                                        variant={"ra-dark-blue"}
                                        className="d-flex align-items-center column-gap-1 "
                                        size="md"
                                        onClick={() => {
                                            setShowMassAddModal(true);
                                        }}>
                                        <FolderPlus className="me-1"></FolderPlus>
                                        Bulk Update
                                    </Button>
                                    <MassAddContentGroupsModal
                                        show={showMassAddModal}
                                        onClose={() => setShowMassAddModal(false)}
                                        radioId={data?.radio_id}
                                        sortedItems={sortedItems}
                                        triggerFormSubmit={handleFormSubmit}
                                        filterFunction={filterFunc}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <DescriptionWithDownloadRow csvHeaders={csvHeaders} data={csvData} filename="Content Testing">
                            <DataDescription>
                                <DataDescription.Station radioId={data?.radio_id} />
                                <DataDescription.DateRange startDate={data?.start_datetime} endDate={data?.end_datetime} />
                                <DataDescription.TimeRange startTime={data?.start_time} endTime={data?.end_time} />
                                <DataDescription.Perspective days={data?.days} />
                                {(data?.min_duration > 0 || data?.max_duration > 0) && (
                                    <DataDescription.Simple
                                        label="Duration"
                                        value={`${formatDuration(data?.min_duration)} - ${data?.max_duration ? formatDuration(data?.max_duration) : "∞"}`}
                                    />
                                )}
                                <DataDescription.Custom>
                                    <div className=" flex-break"></div>
                                    <DataDescription>
                                        <span className=" text-nowrap fw-bold">Total selection:</span>
                                        <DataDescription.Simple
                                            label={data?.data_type === 1 ? "AVG score" : "AVG delta"}
                                            value={data?.data_type === 1 ? toAutoDecimals(totals?.score, 1) : toAutoDecimals(totals?.delta, 2)}
                                        />
                                        <DataDescription.Simple
                                            label="AVG duration"
                                            value={moment.utc(moment.duration(totals?.duration || 0, "seconds").asMilliseconds()).format("m:ss")}
                                        />
                                        <DataDescription.Simple label={"Results"} value={totals?.count} />
                                    </DataDescription>
                                </DataDescription.Custom>

                                {(filterData.keywords || filterData.contentGroups.length > 0) && (
                                    <DataDescription.Custom>
                                        <div className=" flex-break"></div>
                                        <DataDescription>
                                            <span className=" text-nowrap fw-bold">Filtered:</span>
                                            <DataDescription.Simple
                                                label={data?.data_type === 1 ? "AVG score" : "AVG delta"}
                                                value={
                                                    data?.data_type === 1 ? toAutoDecimals(filteredTotals?.score, 1) : toAutoDecimals(filteredTotals?.delta, 2)
                                                }
                                            />
                                            <DataDescription.Simple
                                                label="AVG duration"
                                                value={moment.utc(moment.duration(filteredTotals?.duration || 0, "seconds").asMilliseconds()).format("m:ss")}
                                            />
                                            <DataDescription.Simple label={"Results"} value={filteredTotals?.count} />
                                        </DataDescription>
                                    </DataDescription.Custom>
                                )}

                                {filterData.limit > 0 && (
                                    <DataDescription.Custom>
                                        <div className=" flex-break"></div>
                                        <DataDescription>
                                            <span className=" text-nowrap fw-bold">Limit:</span>
                                            <DataDescription.Simple
                                                label={data?.data_type === 1 ? "AVG score" : "AVG delta"}
                                                value={
                                                    data?.data_type === 1 ? toAutoDecimals(limitedTotals?.score, 1) : toAutoDecimals(limitedTotals?.delta, 2)
                                                }
                                            />
                                            <DataDescription.Simple
                                                label="AVG duration"
                                                value={moment.utc(moment.duration(limitedTotals?.duration || 0, "seconds").asMilliseconds()).format("m:ss")}
                                            />
                                            <DataDescription.Simple label={"Results"} value={limitedTotals?.count} />
                                            {filterData.limit > 0 && <DataDescription.Simple label="Limit" value={Number(filterData.limit)} />}
                                        </DataDescription>
                                    </DataDescription.Custom>
                                )}
                            </DataDescription>
                        </DescriptionWithDownloadRow>
                        <BreaksHeader dataType={data.data_type} sortConfig={sortConfig} requestSort={requestSort} />
                        <VirtualizedWindowContainer containerRef={tableContainerRef} virtualizer={virtualizer}>
                            {virtualizer.getVirtualItems().map(virtualRow => {
                                const item = sortedItems[virtualRow.index];
                                if (!item) return <EmptyRow key={virtualRow.key} text="No breaks" />;
                                return (
                                    <Break
                                        key={virtualRow.key}
                                        radioId={data.radio_id}
                                        dataType={data.data_type}
                                        item={item}
                                        virtualizer={virtualizer}
                                        virtualRow={virtualRow}
                                        setBreaksData={setData}
                                        filterFunction={filterFunc}
                                    />
                                );
                            })}
                        </VirtualizedWindowContainer>
                    </>
                )}
            </ModulePage.DataContent>
        </ModulePage>
    );
}
